import { useAppStore } from '@/store/appStore'
import { useAuthStore } from '@/store/authStore'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as ds from '@/services/dataService'
import { useCanAccessConfig } from '@/hooks/useAuthorization'
import {
  Search,
  Bell,
  Sparkles,
  Server,
  Settings,
  User,
  ChevronDown,
  LogOut,
  Briefcase,
  Clock,
  UserCog,
  KeyRound,
  GraduationCap,
  Moon,
  Home,
  Eye,
  LifeBuoy,
} from 'lucide-react'
import { useState, useRef, useEffect, useSyncExternalStore } from 'react'
import { ModeSwitcher } from './ModeSwitcher'
import { usePersonaReconcile } from './usePersonaReconcile'
import { DemoCompanySwitcher } from './DemoCompanySwitcher'
import { TunnelOrgChip } from './TunnelOrgChip'
import { getDemoCompany } from '@/types/demoCompanies'
import { isInternalSurface } from '@/config/productSurface'
import { isPathAllowed, useModuleEntitlement } from '@/config/moduleEntitlements'
import { isItTicketingPath } from '@/modules/it-ticketing/lib/itTicketingRoutes'
import { subscribePreviewClient, getPreviewClientKey } from '@/config/previewClient'
import { enterClientPreview } from './previewClientActions'

export function Header() {
  // Demo persona reconcile/restore effects — the picker UI now lives in the
  // EnvironmentBanner, but these always-on effects must run regardless of it being
  // open, so they're hosted here (always mounted). Self-gates to demo mode.
  usePersonaReconcile()
  const currentUser = useAppStore((s) => s.currentUser)
  const portfolio = useAppStore((s) => s.selectedPortfolio)
  const currentCompanyId = useAppStore((s) => s.currentCompanyId)
  const appMode = useAppStore((s) => s.appMode)
  // The active demo company (if one is selected). Gates the Client-view button;
  // the top-bar display + switching now live in DemoCompanySwitcher.
  const company = getDemoCompany(currentCompanyId)
  // Re-render the top bar when client-preview toggles, so the internal cockpit
  // (mode switcher, demo badge, the Client-view button itself) flips away the
  // instant preview starts and comes back on exit. isInternalSurface() reads the
  // preview store synchronously; this subscription is what makes React re-run it.
  const previewKey = useSyncExternalStore(subscribePreviewClient, getPreviewClientKey, () => null)
  void previewKey
  const toggleAiPanel = useAppStore((s) => s.toggleAiPanel)
  const navigate = useNavigate()
  // Per-user module entitlement: hide shortcuts into modules this user cannot open.
  const entitlement = useModuleEntitlement()
  const canOpen = (path: string) => isPathAllowed(path, entitlement)
  const location = useLocation()
  // Contract Management is a portfolio-independent module; hide the portfolio
  // breadcrumb on /contracts routes so we don't mislead users (e.g. showing
  // "Revelation Technologies Consulting" over a federal contract workspace).
  const hidePortfolioBreadcrumb = location.pathname.startsWith('/contracts')
  // Inside IT Ticketing the search (CommandPalette) leads with live tickets/people/comments.
  const inItTicketing = isItTicketingPath(location.pathname)

  // Back to the home page (ContextSelector). Clears the active context so the
  // home picker shows cleanly, then navigates to '/'. Present in the top navbar
  // on every page so every module has a reliable, obvious way back home —
  // independent of the sidebar's context-switch affordance.
  function goHome() {
    const s = useAppStore.getState()
    s.selectPortfolio(null)
    s.selectProject(null)
    s.setManagementContext(null)
    navigate('/')
  }
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const { data: rawNotifications } = useQuery({
    queryKey: ['notifications', currentUser.id],
    queryFn: () => ds.getNotifications(),
    staleTime: 60_000,
  })
  const unreadCount = (rawNotifications as Array<{ read: boolean; dismissed: boolean }> | undefined)
    ?.filter(n => !n.read && !n.dismissed).length ?? 0

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const signOut = useAuthStore((s) => s.signOut)
  const authUser = useAuthStore((s) => s.user)
  const canAccessConfig = useCanAccessConfig()

  const roles = ['GlobalAdmin', 'OrgAdmin', 'GlobalViewer', 'ConfigAdmin', 'PortfolioManager', 'PortfolioAnalyst', 'ProjectManager', 'ContractsAdmin', 'FinanceManager', 'Viewer'] as const

  function cycleRole() {
    const setCurrentUser = useAppStore.getState().setCurrentUser
    const idx = roles.indexOf(currentUser.role as typeof roles[number])
    const nextRole = roles[(idx + 1) % roles.length]!
    setCurrentUser({ ...currentUser, role: nextRole })
    setUserMenuOpen(false)
  }

  function handleSignOut() {
    setUserMenuOpen(false)
    signOut()
  }

  return (
    <header className="h-14 bg-white border-b border-border flex items-center px-2 gap-1.5 sm:px-4 sm:gap-3 flex-shrink-0 z-20">
      {/* Home — always present in the top navbar; returns to the context picker. */}
      <button
        onClick={goHome}
        title="Back to home"
        aria-label="Back to home"
        className="flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-text-secondary hover:text-brand-600 hover:bg-brand-50 transition-colors flex-shrink-0"
      >
        <Home size={18} />
        <span className="hidden sm:inline text-body-sm font-medium">Home</span>
      </button>
      {/* Breadcrumb (hidden on mobile — context lives in the bottom tab bar; the
          div stays as a flex spacer so the actions right-align). */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* The production "Active organization" switcher was removed from the top
            bar per product direction — org scope is chosen on the home context
            picker; the top-bar dropdown was redundant chrome in the PM studio.
            (activeOrganizationId still drives scoping; only the switcher UI is gone.) */}
        {/* Company-identity switcher — a dropdown that switches the company
            identity in place. Serves demo ("Demo") AND development/quality
            ("Organization" — same identities, tunnel continuity). Self-gated;
            client preview never shows it.
            hidden below sm: at phone widths the top bar has no room for these
            chips (measured 390px: chip and search overlap by 33px) — company
            switching lives in the EnvironmentBanner row on phones instead. */}
        <div className="hidden sm:flex items-center gap-2 min-w-0">
          <DemoCompanySwitcher />
          {/* Sandbox org dropdown — the curated REAL-org list (shared with the
              homepage picker), switched in place so the page stays mounted and
              re-scopes. Self-gated to sandbox + internal surface. Together the
              three switchers cover all 5 modes: demo/dev/quality above,
              sandbox here, production via ActiveOrgSwitcher. */}
          <TunnelOrgChip />
        </div>
        {portfolio && !hidePortfolioBreadcrumb && (
          <>
            <span className="hidden sm:inline text-body-sm text-text-secondary truncate">
              {portfolio.path}
            </span>
          </>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        {/* Opens the command palette (page + action search). One search reachable
            by click or Ctrl+K, so an admin can find any page (incl. the Admin &
            Reporting surfaces) by name without knowing which section it's in. */}
        <button
          onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
          className="h-9 px-3 rounded-lg border border-border hover:bg-surface-muted transition-colors flex items-center gap-2 text-text-secondary text-body-sm"
          aria-label={inItTicketing ? 'Search tickets, people and pages' : 'Search pages and actions'}
        >
          <Search size={16} />
          <span className="hidden md:inline">{inItTicketing ? 'Search tickets...' : 'Search pages...'}</span>
          <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 rounded border border-border text-[10px] text-text-tertiary bg-surface-muted ml-4">
            Ctrl+K
          </kbd>
        </button>
      </div>

      {/* Environment pill (internal surface only) — color-coded by mode. The act-as
          controls for every non-production environment (demo persona, sandbox user)
          + any lingering client preview live in the EnvironmentBanner under this bar,
          not as standalone top-bar controls. */}
      <ModeSwitcher />

      {/* Client view (internal surface, ANY mode — tunnel model) — flips THIS
          instance into the client's product view (Retool Edit→Preview pattern):
          hides the internal cockpit so you can show a client a real, chrome-free
          product walkthrough without switching companies. The org identity now
          persists across the lifecycle, so previewing the client product is valid
          at any stage (matches the home-hero button, 66a28b37). Exit lives in the
          EnvironmentBanner strip. Self-hides once preview starts
          (isInternalSurface() → false). Also hidden in the production edition —
          a live production tenant is already the client's real product, so the
          "preview as client" tunnel is meaningless (and confusing) there. */}
      {isInternalSurface() && appMode !== 'production' && company && currentCompanyId && (
        <button
          onClick={() => enterClientPreview(currentCompanyId)}
          title="Preview this as the client's live product — hides the internal cockpit"
          aria-label="Switch to client product view"
          className="h-9 px-3 rounded-lg border border-border hover:bg-surface-muted hover:text-brand-600 active:scale-95 transition-colors flex items-center gap-2 text-text-secondary text-body-sm flex-shrink-0"
        >
          <Eye size={16} />
          <span className="hidden md:inline">Client view</span>
        </button>
      )}

      {/* The display-currency toggle (US$/A$) is NOT here — it lives in the page
          header of money-bearing pages (PageHeader), right above their tables, so
          it shows only where money is displayed. */}

      {/* Divider — separates the context switchers (mode / persona) from the
          tools + account cluster, so the top bar reads as two groups. */}
      <span className="hidden sm:block h-5 w-px bg-border mx-0.5 flex-shrink-0" aria-hidden="true" />

      {/* XPM Agent toggle */}
      <button
        onClick={toggleAiPanel}
        className="h-9 px-3 rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors flex items-center gap-2 text-body-sm font-medium"
        aria-label="Toggle XPM Agent"
      >
        <Sparkles size={16} />
        <span className="hidden md:inline">XPM Agent</span>
      </button>

      {/* Help Center — the one-click path to /help (Connor 2026-07-21: "How do
          I get to help center by button click?" — before this, the only routes
          were a page-? footer link or Ctrl+K). Sits with the tools cluster. */}
      {canOpen('/help') && (
        <button
          onClick={() => navigate('/help')}
          className="h-9 min-w-9 px-1.5 rounded-lg hover:bg-surface-muted transition-colors flex flex-col items-center justify-center gap-[3px] text-text-secondary hover:text-brand-600"
          aria-label="Help Center"
          title="Help Center — every guide, tour, and journey"
        >
          <LifeBuoy size={15} />
          <span className="text-[9px] leading-none font-medium tracking-wide">Help</span>
        </button>
      )}

      {/* Notifications */}
      {canOpen('/notifications') && (
        <button
          onClick={() => navigate('/notifications')}
          className="relative h-9 w-9 rounded-lg hover:bg-surface-muted transition-colors flex items-center justify-center text-text-secondary"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* User menu */}
      <div className="relative" ref={userMenuRef}>
        <button
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          className="flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-lg border border-border hover:bg-surface-muted transition-colors"
          aria-label="User menu"
          aria-expanded={userMenuOpen}
          title="Your account"
        >
          <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-[11px] font-semibold">
            {currentUser.initials}
          </div>
          <span className="hidden lg:inline text-body-sm font-medium">{currentUser.name}</span>
          <ChevronDown size={14} className="text-text-secondary" />
        </button>

        {userMenuOpen && (
          <div className="absolute right-0 top-11 w-64 bg-white rounded-lg shadow-dropdown border border-border py-1 animate-slide-in-up z-50">
            <div className="px-3 py-2.5 border-b border-border flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center text-white text-[12px] font-semibold flex-shrink-0">
                {currentUser.initials}
              </div>
              <div className="min-w-0">
                <div className="text-body-sm font-medium truncate">{currentUser.name}</div>
                <div className="text-[11px] text-text-secondary truncate">{currentUser.role}</div>
                {authUser?.email && (
                  <div className="text-[11px] text-text-tertiary truncate">{authUser.email}</div>
                )}
              </div>
            </div>

            {/* Personal */}
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-text-tertiary mt-1">
              Personal
            </div>
            {canOpen('/my-work') && (
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/my-work') }}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <Briefcase size={14} />
                My Work
              </button>
            )}
            {canOpen('/timesheet/my') && (
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/timesheet/my') }}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <Clock size={14} />
                My Timesheet
              </button>
            )}
            {canOpen('/it-ticketing') && (
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/it-ticketing/my-delegations') }}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <UserCog size={14} />
                My delegations
              </button>
            )}
            {canOpen('/notifications') && (
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/notifications') }}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <Bell size={14} />
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* Profile / preferences */}
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-text-tertiary mt-2">
              Profile
            </div>
            <button
              onClick={() => { setUserMenuOpen(false); navigate('/my-profile') }}
              className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
            >
              <User size={14} />
              My profile
            </button>
            {canOpen('/config/expertise') && (
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/config/expertise') }}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <GraduationCap size={14} />
                Expertise catalog
              </button>
            )}
            {canOpen('/timesheet/settings') && (
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/timesheet/settings') }}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <Settings size={14} />
                Preferences
              </button>
            )}
            {/* Demo-only affordance: cycling your own role is a walkthrough tool,
                not a production capability. Hidden in the production edition (a
                real user's role comes from their roster row, not a local toggle). */}
            {isInternalSurface() && appMode !== 'production' && (
              <button
                onClick={cycleRole}
                className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              >
                <KeyRound size={14} />
                Switch role (Demo)
              </button>
            )}
            <button
              onClick={() => { setUserMenuOpen(false) }}
              className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
              title="Theme switching is wired up but defaults to light"
            >
              <Moon size={14} />
              Toggle theme
            </button>

            {canAccessConfig && canOpen('/developer/api-explorer') && (
              <>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-text-tertiary mt-2">
                  Developer
                </div>
                <button
                  onClick={() => { setUserMenuOpen(false); navigate('/developer/api-explorer') }}
                  className="w-full text-left px-3 py-2 text-body-sm hover:bg-surface-muted flex items-center gap-2"
                >
                  <Server size={14} />
                  API Explorer
                </button>
              </>
            )}

            <div className="border-t border-border my-1" />
            <button
              onClick={handleSignOut}
              className="w-full text-left px-3 py-2 text-body-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
